import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Upload, DollarSign, Users, CheckCircle2, XCircle, FileText, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { StatusPill, EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";

const fmtMoney = (n) => `$${Number(n || 0).toFixed(4)}`;

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";

function StatCard({ label, value, icon: Icon, hint }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
        <Icon size={18} className="text-slate-500" />
      </div>
      <div className="font-display text-3xl tracking-tight text-white">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export default function AdminRoyalties() {
  const [tab, setTab] = useState("upload");
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [decision, setDecision] = useState(null);
  const [decisionForm, setDecisionForm] = useState({ status: "paid", reference: "", reason: "" });
  const [decisionSaving, setDecisionSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, w] = await Promise.all([
        api.get("/admin/royalties/summary"),
        api.get("/admin/royalties/batches"),
        api.get("/admin/withdrawals"),
      ]);
      setSummary(s.data);
      setBatches(b.data);
      setWithdrawals(w.data);
    } catch (e) {
      toast.error("Failed to load royalties");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/royalties/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported} lines · matched ${data.matched} · unmatched ${data.unmatched}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "CSV upload failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openDecision = (w, status) => {
    setDecision(w);
    setDecisionForm({ status, reference: "", reason: "" });
  };

  const submitDecision = async (e) => {
    e.preventDefault();
    if (!decision) return;
    setDecisionSaving(true);
    try {
      await api.patch(`/admin/withdrawals/${decision.request_id}`, decisionForm);
      toast.success(decisionForm.status === "paid" ? "Withdrawal marked paid" : "Withdrawal rejected");
      setDecision(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    } finally {
      setDecisionSaving(false);
    }
  };

  const pendingCount = withdrawals.filter((w) => w.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="admin-royalties">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Royalties</h2>
          <p className="mt-1.5 text-slate-400">
            Upload monthly DSR statements, review earnings and process artist withdrawal requests.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-medium px-5 py-2.5 hover:bg-slate-200 active:scale-[0.98] transition-all shrink-0 cursor-pointer">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : "Upload DSR CSV"}
          <input
            data-testid="royalty-csv-input"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={uploadCsv}
            disabled={uploading}
          />
        </label>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total net" value={fmtMoney(summary?.total_net)} icon={DollarSign} hint="Across all uploads" />
            <StatCard label="Matched" value={fmtMoney(summary?.total_matched_net)} icon={CheckCircle2} hint={`${summary?.per_user?.filter((r) => r.user_id).length || 0} artists`} />
            <StatCard label="Unmatched" value={fmtMoney(summary?.unmatched_net)} icon={XCircle} hint="ISRC / UPC not in catalog" />
            <StatCard label="Pending payouts" value={pendingCount} icon={Users} hint="Withdrawal requests awaiting review" />
          </div>

          <div className="flex items-center gap-2 border-b border-white/10">
            {[
              { key: "upload", label: "Batches" },
              { key: "artists", label: "By artist" },
              { key: "withdrawals", label: `Withdrawals ${pendingCount ? `(${pendingCount})` : ""}` },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`royalty-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm border-b-2 transition-colors -mb-px ${
                  tab === t.key ? "border-white text-white" : "border-transparent text-slate-400 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "upload" && (
            batches.length === 0 ? (
              <EmptyState
                title="No DSR uploaded yet"
                desc="Upload a Digital Sales Details CSV (22 columns starting with Period, Delivered By, Retailer…)."
              />
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <th className={th}>Filename</th>
                    <th className={th}>Imported</th>
                    <th className={th}>Matched</th>
                    <th className={th}>Unmatched</th>
                    <th className={th}>Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.batch_id} data-testid={`batch-row-${b.batch_id}`}>
                      <td className={td}>
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-slate-500" />
                          <span className="text-white">{b.filename}</span>
                        </div>
                      </td>
                      <td className={td}>{b.imported}</td>
                      <td className={td + " text-emerald-300"}>{b.matched}</td>
                      <td className={td + " text-amber-300"}>{b.unmatched}</td>
                      <td className={td}>{fmtDate(b.uploaded_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )
          )}

          {tab === "artists" && (
            summary?.per_user?.length === 0 ? (
              <EmptyState title="No earnings yet" desc="Upload a DSR to see per-artist earnings." />
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <th className={th}>Artist / Label</th>
                    <th className={th}>Lines</th>
                    <th className={th}>Gross</th>
                    <th className={th}>Net</th>
                    <th className={th}>Paid</th>
                    <th className={th}>Unpaid</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.per_user.map((r, i) => (
                    <tr key={r.user_id || `unm-${i}`} data-testid={`royalty-artist-${r.user_id || 'unmatched'}`}>
                      <td className={td}>
                        <div className={r.user_id ? "text-white" : "text-amber-300"}>{r.display_name}</div>
                        <div className="text-xs text-slate-500">{r.email || (r.user_id ? "" : "ISRC / UPC not in catalog")}</div>
                      </td>
                      <td className={td}>{r.lines}</td>
                      <td className={td}>{fmtMoney(r.gross)}</td>
                      <td className={td}>{fmtMoney(r.net)}</td>
                      <td className={td + " text-emerald-300"}>{fmtMoney(r.paid)}</td>
                      <td className={td + " text-amber-300"}>{fmtMoney(r.unpaid)}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )
          )}

          {tab === "withdrawals" && (
            withdrawals.length === 0 ? (
              <EmptyState title="No withdrawal requests" desc="Artists and labels will request payouts from their Royalties tab." />
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <th className={th}>Requestor</th>
                    <th className={th}>Amount</th>
                    <th className={th}>PayPal / Bank</th>
                    <th className={th}>Status</th>
                    <th className={th}>Requested</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((w) => (
                    <tr key={w.request_id} data-testid={`withdrawal-row-${w.request_id}`}>
                      <td className={td}>
                        <div className="text-white">{w.display_name}</div>
                        <div className="text-xs text-slate-500">{w.email}</div>
                      </td>
                      <td className={td + " text-white font-medium"}>{fmtMoney(w.amount)}</td>
                      <td className={td}>
                        <div className="text-xs text-slate-300">{w.paypal_email}</div>
                        <div className="text-[11px] text-slate-500">{w.bank_name} · {w.account_number.slice(-4).padStart(w.account_number.length, "•")}</div>
                      </td>
                      <td className={td}><StatusPill status={w.status === "paid" ? "Live" : w.status === "rejected" ? "Rejected" : "Pending"} /></td>
                      <td className={td}>{fmtDate(w.created_at)}</td>
                      <td className={td + " text-right"}>
                        {w.status === "pending" ? (
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              data-testid={`withdraw-reject-${w.request_id}`}
                              onClick={() => openDecision(w, "rejected")}
                              className="rounded-lg border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 text-slate-300 hover:text-red-400 px-3 py-1.5 text-xs"
                            >
                              Reject
                            </button>
                            <button
                              data-testid={`withdraw-approve-${w.request_id}`}
                              onClick={() => openDecision(w, "paid")}
                              className="rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 px-3 py-1.5 text-xs"
                            >
                              Mark paid
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                            {w.reference && <ExternalLink size={12} />}{w.reference || (w.reason || "")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )
          )}
        </>
      )}

      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-md" data-testid="withdraw-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {decisionForm.status === "paid" ? "Mark withdrawal as paid" : "Reject withdrawal"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {decision && `${decision.display_name} · ${fmtMoney(decision.amount)}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDecision} className="space-y-3 mt-2">
            {decisionForm.status === "paid" ? (
              <div>
                <label className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 block">Payment reference</label>
                <input
                  data-testid="withdraw-reference"
                  value={decisionForm.reference}
                  onChange={(e) => setDecisionForm((f) => ({ ...f, reference: e.target.value }))}
                  placeholder="PayPal / bank transfer ID"
                  className={inputCls}
                />
              </div>
            ) : (
              <div>
                <label className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 block">Reason (visible to artist)</label>
                <textarea
                  data-testid="withdraw-reason"
                  value={decisionForm.reason}
                  onChange={(e) => setDecisionForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="Explain why this request is being rejected"
                  className={inputCls + " min-h-[80px]"}
                />
              </div>
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={() => setDecision(null)}
                className="rounded-full border border-white/15 text-white text-sm px-5 py-2.5 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                data-testid="withdraw-decision-submit"
                type="submit"
                disabled={decisionSaving}
                className={`inline-flex items-center justify-center gap-2 rounded-full font-medium px-6 py-2.5 text-sm ${
                  decisionForm.status === "paid"
                    ? "bg-emerald-500 text-black hover:bg-emerald-400"
                    : "bg-red-500 text-white hover:bg-red-400"
                } disabled:opacity-60`}
              >
                {decisionSaving ? <Loader2 size={16} className="animate-spin" /> : decisionForm.status === "paid" ? "Confirm paid" : "Reject request"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
